import { FC, ReactElement, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { skipToken } from '@reduxjs/toolkit/query';

import { setMapMode } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';
import { clearUtkast, fetchUtkast } from 'store/utkastSlice';
import { clearVinjett, fetchArkiveradResurs, fetchMellanlagradResurs } from 'store/vinjettSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useInitialFocusOnExtent } from 'hooks/useInitialFocusOnExtent';

import { isOmrade, isSammansatt } from 'components/bebyggelseobjekt/BebyggelseobjektPresentation';
import BebyggelseBeskrivningSection from 'components/bebyggelseobjekt/sections/BebyggelseBeskrivningSection';
import BebyggelseFastigheterSection from 'components/bebyggelseobjekt/sections/BebyggelseFastigheterSection';
import BebyggelseHistorikSection from 'components/bebyggelseobjekt/sections/BebyggelseHistorikSection';
import BebyggelseSkyddstypSection from 'components/bebyggelseobjekt/sections/BebyggelseSkyddstypSection';
import BebyggelseTitleSection from 'components/bebyggelseobjekt/sections/BebyggelseTitleSection';
import RegistreringshistorikSection from 'components/bebyggelseobjekt/sections/RegistreringshistorikSection';
import SammansattBebyggelseSkyddbestammelserSection from 'components/bebyggelseobjekt/sections/SammansattBebyggelseSkyddbestammelserSection';
import BebyggelseDokumentationSection from 'components/bebyggelseobjekt/sections/dokumentation/BebyggelseDokumentationSection';
import {
  DrawerBody,
  DrawerContainer,
  DrawerSection,
  NavigationDrawerHead
} from 'components/drawerSections/DrawerSections';
import { replaceUtkastFeaturesInActiveSource } from 'components/map/Layers';
import UtkastSammansattPresentation from 'components/utkast/utkastPresentation/UtkastSammansattPresentation';
import UtkastStatusSection from 'components/utkast/utkastPresentation/sections/UtkastStatusSection';

import { Socken } from 'models';

import { BebyggelseobjektgruppEnum, MapModeEnum, VinjettSourceEnum } from 'enums';

import { useGetDokumentationQuery } from 'api/endpoints/dokumentationSlice';
import { useReserveBebyggelseutkastMutation } from 'api/endpoints/reservationSlice';
import { renderWithParagraphs } from 'utilities/renderWithParagraphs';

const UtkastPresentation: FC = (): ReactElement => {
  const { id } = useParams();
  const dispatch = useAppDispatch();

  const utkastData = useAppSelector((state: RootState) => state.utkast.data);
  const { data: vinjettData, requestStatus: vinjettStatus } = useAppSelector((state: RootState) => state.vinjett);
  const domaner = useAppSelector((state: RootState) => state.bebyggelseobjektDomaner.data);

  const [, results] = useReserveBebyggelseutkastMutation({ fixedCacheKey: 'utkast-reservation' });
  const { isLoading: reservationIsLoading } = results;

  // Initering för att veta hur många dokument som finns i mellanregistret
  const { data: dokumentation } = useGetDokumentationQuery(utkastData?.utkast_id ?? skipToken);

  useEffect(() => {
    dispatch(setMapMode(MapModeEnum.UTKAST_PRESENTATION));

    return () => {
      dispatch(clearUtkast());
      replaceUtkastFeaturesInActiveSource([]);
      dispatch(setMapMode(MapModeEnum.DEFAULT));
    };
  }, [dispatch]);

  useInitialFocusOnExtent(
    useCallback(() => utkastData && replaceUtkastFeaturesInActiveSource([utkastData]), [utkastData])
  );

  useEffect(() => {
    if (id && !reservationIsLoading) {
      dispatch(fetchUtkast(id));
    }
  }, [id, dispatch, reservationIsLoading]);

  useEffect(() => {
    if (utkastData?.vinjett?.typ === VinjettSourceEnum.MELLANLAGRAD_RESURS) {
      dispatch(fetchMellanlagradResurs(utkastData.vinjett.url));
    } else if (utkastData?.vinjett?.typ === VinjettSourceEnum.ARKIVERAD_RESURS) {
      dispatch(fetchArkiveradResurs(utkastData.vinjett.url));
    } else {
      dispatch(clearVinjett());
    }
  }, [dispatch, utkastData?.vinjett]);

  if (utkastData === undefined) {
    return <></>;
  }

  return isSammansatt(utkastData) ? (
    <UtkastSammansattPresentation utkastData={utkastData} domaner={domaner} />
  ) : (
    <DrawerContainer>
      <NavigationDrawerHead />
      <DrawerBody>
        <UtkastStatusSection utkast={utkastData} />
        <BebyggelseTitleSection
          skyddstyp={utkastData.skydd.skyddstyp}
          skyddstypNamn={utkastData.skydd.skyddstyp_namn}
          data={utkastData}
          lm_andamal={
            utkastData.lm_registerbyggnad?.byggnadsandamal
              ? utkastData.lm_registerbyggnad?.byggnadsandamal[0].andamal
              : undefined
          }
          socken={utkastData.geografisk_indelning.socken.map((socken: Socken) => socken.socken_namn).join(', ')}
          domaner={domaner}
          vinjett={vinjettData}
          vinjettRequestStatus={vinjettStatus}
        />
        <BebyggelseSkyddstypSection
          ansvarig_myndighet={
            domaner?.skyddstyper.find((s) => s.skyddstyp === utkastData.skydd.skyddstyp)?.ansvarig_myndighet
          }
          motivering={utkastData.skydd.motivering}
          karaktarsdrag={utkastData.karaktarsdrag}
          skyddstyp={utkastData.skydd.skyddstyp}
          lokal_klassificering={utkastData.lokal_klassificering}
          sammansattParentId={utkastData.sammansatt_parent_id}
          sammansattParentNamn={utkastData.sammansatt_parent_namn}
          skydd_subtyp={utkastData.skydd.kyrkligtkulturminneskyddstyp_namn}
        />
        <SammansattBebyggelseSkyddbestammelserSection
          sammansattParentId={utkastData.sammansatt_parent_id}
          skyddsbestammelser={utkastData.sammansatt?.skyddsbestammelser}
        />
        <BebyggelseBeskrivningSection
          domaner={domaner}
          beskrivning={utkastData.beskrivning || ''}
          egenskaper={
            utkastData.bebyggelseobjektgrupp !== BebyggelseobjektgruppEnum.OMRADE ? utkastData.egenskaper : undefined
          }
        />
        <BebyggelseHistorikSection
          historik={utkastData.historik || ''}
          handelser={utkastData.handelser}
          domaner={domaner}
        />

        <BebyggelseDokumentationSection
          uri={utkastData.publicerad_uri}
          utkastId={utkastData.utkast_id}
          includeRitningar={false}
          totalDocuments={
            dokumentation
              ? dokumentation.length + utkastData.dokumentation_ref.arkiv_ref.length
              : utkastData.dokumentation_ref.arkiv_ref.length
          }
        />

        {isOmrade(utkastData) && <BebyggelseFastigheterSection fastigheter={utkastData.berorda_fastigheter} />}

        <DrawerSection>
          <h2 className="tw-heading-md tw-mb-4">Referenser</h2>
          {renderWithParagraphs(utkastData.referenser || '–')}
        </DrawerSection>

        <RegistreringshistorikSection bebyggelseobjektId={utkastData.utkast_id} />
      </DrawerBody>
    </DrawerContainer>
  );
};

export default UtkastPresentation;
